import { useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import LMP from "assets/icons/LMP-header-mobile.svg";
import { useAuth } from "contexts/AuthProvider";
import { Link, useNavigate } from "react-router-dom";
import { GlobalHeader, NavItem } from "types/cms/GlobalHeader";
import { useNavigateToUrl } from "hooks/useNavigateToUrl";

const Header = ({ headerData }: { headerData: GlobalHeader }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const toggleDrawer = (open: boolean) => () => {
    setIsDrawerOpen(open);
  };

  const navigateToUrl = useNavigateToUrl();

  const renderAuthButtons = () =>
    user ? (
      <>
        <Button
          variant="outlined"
          sx={{
            fontWeight: "700",
            borderRadius: 28,
            px: 3,
            color: "#080A52",
            border: "2px solid #080A52",
            textTransform: "none",
          }}
          onClick={logout}
        >
          Logout
        </Button>
        <Button
          variant="contained"
          sx={{
            fontWeight: "700",
            borderRadius: 28,
            px: 3,
            color: "#fff",
            textTransform: "none",
            boxShadow: "4px 4px 8px 0px rgba(0, 0, 0, 0.2)",
          }}
          onClick={() => navigate("/dashboard")}
        >
          Dashboard
        </Button>
      </>
    ) : (
      <>
        {headerData?.headerCtaButton1?.link?.label && (
          <Button
            variant="outlined"
            sx={{
              borderRadius: 28,
              px: 3,
              color: "#080A52",
              border: "2px solid #080A52",
              textTransform: "none",
              fontWeight: "700",
            }}
            component={Link}
            to={
              headerData?.headerCtaButton1?.link?.type === "reference"
                ? headerData?.headerCtaButton1?.link?.reference?.value?.slug ||
                  ""
                : headerData?.headerCtaButton1?.link?.url || ""
            }
            target={
              headerData?.headerCtaButton1?.link?.newTab ? "_blank" : undefined
            }
          >
            {headerData?.headerCtaButton1?.link?.label}
          </Button>
        )}
        {headerData?.headerCtaButton2?.link?.label && (
          <Button
            variant="contained"
            sx={{
              borderRadius: 28,
              px: 3,
              color: "#fff",
              textTransform: "none",
              fontWeight: "700",
              boxShadow: "4px 4px 8px 0px rgba(0, 0, 0, 0.2)",
            }}
            component={Link}
            target={
              headerData?.headerCtaButton2?.link?.newTab ? "_blank" : undefined
            }
            to={
              headerData?.headerCtaButton2?.link?.type === "reference"
                ? headerData?.headerCtaButton2?.link?.reference?.value?.slug ||
                  ""
                : headerData?.headerCtaButton2?.link?.url || ""
            }
          >
            {headerData?.headerCtaButton2?.link?.label}
          </Button>
        )}
      </>
    );

  return (
    <AppBar
      position="sticky"
      elevation={0}
      className="header-menu"
      sx={{
        backgroundColor: "#fff",
        height: "100px",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Toolbar
        sx={{
          paddingLeft: isMobile ? "10px" : "45px",
          paddingRight: isMobile ? "10px" : "45px",
        }}
      >
        <Grid sx={{ flexGrow: 1 }}>
          <Link to="/">
            <img
              src={`${isMobile ? `${headerData?.logoMobile?.url}` : `${headerData?.logoDesktop?.url}`}`}
              alt={headerData?.logoDesktop?.alt || ""}
              style={{ width: isMobile ? "48px" : "190px" }}
            />
          </Link>
        </Grid>
        {isMobile ? (
          <>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer(!isDrawerOpen)}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                "& .bar": {
                  width: "35px",
                  height: "6px",
                  backgroundColor: "#080A52",
                },
              }}
            >
              <div className="bar" />
              <div className="bar" />
              <div className="bar" />
            </IconButton>

            <Drawer
              anchor="right"
              open={isDrawerOpen}
              onClose={toggleDrawer(false)}
            >
              <Box
                sx={{
                  width: "100vw",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  padding: 2,
                  backgroundColor: "#fff",
                  background: "linear-gradient(to bottom, #080A52, #050529)",
                }}
                role="presentation"
                onClick={toggleDrawer(false)}
                onKeyDown={toggleDrawer(false)}
              >
                <Box
                  sx={{
                    padding: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Link to="/">
                    <img
                      src={`${headerData?.logoMobile?.url}`}
                      alt="logo"
                      style={{ width: "48px" }}
                    />
                  </Link>
                  {/* x icon */}
                  <div
                    className="xIcon"
                    style={{
                      position: "relative",
                      width: "35px",
                      height: "35px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <div
                      className="xbar"
                      style={{
                        position: "absolute",
                        width: "35px",
                        height: "4px",
                        backgroundColor: "#fff",
                        transform: "rotate(45deg)",
                      }}
                    ></div>
                    <div
                      className="xbar"
                      style={{
                        position: "absolute",
                        width: "35px",
                        height: "4px",
                        backgroundColor: "#fff",
                        transform: "rotate(-45deg)",
                      }}
                    ></div>
                  </div>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    marginTop: 7,
                  }}
                >
                  {headerData?.navItems?.map((item: NavItem) => (
                    <Button
                      key={`nav-item-mobile-${item.link.label}`}
                      sx={{
                        color: "white",
                        textTransform: "none",
                        fontSize: "18px",
                        fontFamily: "Neue Haas Grotesk Display medium",
                      }}
                      onClick={() => navigateToUrl(item.link)}
                    >
                      {item.link.label}
                    </Button>
                  ))}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: "20px",
                    justifyContent: "center",
                    marginTop: 5,
                  }}
                >
                  {user ? (
                    <>
                      <Box
                        sx={{
                          borderRadius: 28,
                          px: 3,
                          py: 1,
                          color: "#fff",
                          border: "2px solid #fff",
                          textTransform: "none",
                        }}
                        onClick={logout}
                      >
                        <Typography>Logout</Typography>
                      </Box>
                      <Box
                        sx={{
                          borderRadius: 28,
                          px: 3,
                          py: 1,
                          color: "#fff",
                          textTransform: "none",
                          backgroundColor: "#3399FF",
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={() => navigate("/dashboard")}
                      >
                        <Typography>Dashboard</Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        sx={{
                          borderRadius: 28,
                          px: 3,
                          color: "#fff",
                          border: "2px solid #fff",
                          textTransform: "none",
                        }}
                        component={Link}
                        target={
                          headerData?.headerCtaButton1?.link?.newTab
                            ? "_blank"
                            : undefined
                        }
                        to={
                          headerData?.headerCtaButton1?.link?.type ===
                          "reference"
                            ? headerData?.headerCtaButton1?.link?.reference
                                ?.value?.slug || ""
                            : headerData?.headerCtaButton1?.link?.url || ""
                        }
                      >
                        {headerData?.headerCtaButton1?.link?.label}
                      </Button>
                      <Button
                        variant="contained"
                        sx={{
                          borderRadius: 28,
                          px: 3,
                          color: "#fff",
                          textTransform: "none",
                        }}
                        component={Link}
                        target={
                          headerData?.headerCtaButton2?.link?.newTab
                            ? "_blank"
                            : undefined
                        }
                        to={
                          headerData?.headerCtaButton2?.link?.type ===
                          "reference"
                            ? headerData?.headerCtaButton2?.link?.reference
                                ?.value?.slug || ""
                            : headerData?.headerCtaButton2?.link?.url || ""
                        }
                      >
                        {headerData?.headerCtaButton2?.link?.label}
                      </Button>
                    </>
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "auto",
                    marginBottom: "50px",
                  }}
                >
                  <img src={LMP} alt="lmp logo" />
                </Box>
              </Box>
            </Drawer>
          </>
        ) : (
          <Grid container spacing={2}>
            <Box
              sx={{
                display: "flex",
                gap: 5,
                flexDirection: "row",
                marginRight: 5,
                color: "#000",
                alignItems: "center",
              }}
            >
              {headerData?.navItems
                ?.filter((item: NavItem) => item.link.label !== "Home")
                .map((item: NavItem) => (
                  <Button
                    key={`nav-item-desktop-${item.link.label}`}
                    sx={{
                      color: "black",
                      textTransform: "none",
                      fontSize: "18px",
                      fontFamily: "Neue Haas Grotesk Display medium",
                    }}
                    onClick={() => navigateToUrl(item.link)}
                  >
                    {item.link.label}
                  </Button>
                ))}
            </Box>
            {renderAuthButtons()}
          </Grid>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
